import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Pagination, PAGE_SIZE } from "@/components/Pagination";
import {
  Plus,
  Package,
  Truck,
  CalendarBlank,
  PaperPlaneTilt,
} from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { AppHeader } from "@/components/AppHeader";
import { FilterBar } from "@/components/FilterBar";
import { api } from "@/lib/api";

const FILTERS = ["All", "Draft", "Ordered", "Partial", "Received", "Cancelled"];
const VIEWS = ["Cards", "Table"];

export const PO_BADGE = {
  Draft: "bg-zinc-500/10 text-zinc-400 border-zinc-500/25",
  Ordered: "bg-amber-500/10 text-amber-400 border-amber-500/25",
  Partial: "bg-blue-500/10 text-blue-400 border-blue-500/25",
  Received: "bg-emerald-500/10 text-emerald-400 border-emerald-500/25",
  Cancelled: "bg-red-500/10 text-red-400 border-red-500/25",
};

export const PO_ACCENT = {
  Draft: "bg-zinc-600",
  Ordered: "bg-amber-500",
  Partial: "bg-blue-500",
  Received: "bg-emerald-500",
  Cancelled: "bg-red-500",
};

const lineCounts = (po) => {
  const total = po.lines.reduce((n, l) => n + (l.qty || 0), 0);
  const got = po.lines.reduce((n, l) => n + (l.received_qty || 0), 0);
  return { total, got, pct: total ? Math.round((got / total) * 100) : 0 };
};

export default function PurchaseOrders() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [page, setPage] = useState(1);
  const [supplierCount, setSupplierCount] = useState(0);
  const [filter, setFilter] = useState([]);
  const [view, setView] = useState("Cards");
  const [q, setQ] = useState("");

  const load = useCallback(async () => {
    const params = {};
    if (q.trim()) params.q = q.trim();
    const [pos, sups] = await Promise.all([
      api.get("/purchase-orders", { params }),
      api.get("/suppliers"),
    ]);
    setItems(pos.data);
    setSupplierCount(sups.data.length);
  }, [q]);

  const shown = items.filter(
    (po) => filter.length === 0 || filter.includes(po.status),
  );

  const totalPages = Math.max(1, Math.ceil(shown.length / PAGE_SIZE));
  const curPage = Math.min(page, totalPages);
  const paged = shown.slice((curPage - 1) * PAGE_SIZE, curPage * PAGE_SIZE);

  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [load]);

  const stats = useMemo(() => {
    let open = 0;
    let awaiting = 0;
    let received = 0;
    items.forEach((po) => {
      const { total, got } = lineCounts(po);
      if (po.status === "Received") received += got;
      else if (po.status !== "Cancelled") {
        open += 1;
        awaiting += Math.max(total - got, 0);
      }
    });
    return { open, awaiting, received };
  }, [items]);

  return (
    <div className="grain min-h-screen">
      <AppHeader title="Inventory / Purchase orders" />
      <main className="relative z-10 pb-28 pt-6">
        <div
          className="rounded-lg border border-zinc-800 bg-zinc-900 p-5"
          data-testid="po-header-card"
        >
          <div className="flex flex-wrap items-end justify-between gap-4">
            <div>
              <div className="text-xs uppercase tracking-widest text-zinc-500">
                Inventory
              </div>
              <h1 className="text-2xl font-bold tracking-tight sm:text-3xl">
                Purchase Orders
              </h1>
              <p className="mt-1 text-sm text-zinc-500">
                Order slabs and sinks from suppliers, then receive them piece by
                piece into stock
              </p>
            </div>
          </div>

          <div
            className="mt-5 flex flex-wrap gap-2 border-t border-zinc-800 pt-4"
            data-testid="po-actions"
          >
            <Button
              data-testid="add-po-button"
              onClick={() => navigate("/inventory/purchase-orders/new")}
              className="h-12 bg-indigo-500 text-xs font-bold uppercase tracking-widest text-white hover:bg-indigo-400"
            >
              <Plus size={15} weight="bold" className="mr-2" /> New PO
            </Button>
            <Button
              data-testid="toggle-suppliers-button"
              variant="outline"
              onClick={() => navigate("/inventory/suppliers")}
              className="h-12 border-zinc-700 bg-transparent px-4 text-[11px] uppercase tracking-widest text-zinc-300 transition-colors duration-150 hover:bg-zinc-800"
            >
              <Truck size={15} weight="bold" className="mr-2" /> Suppliers (
              {supplierCount})
            </Button>
          </div>
        </div>

        <div className="mt-6 grid grid-cols-2 gap-3 sm:grid-cols-4">
          {[
            ["Orders", items.length, "text-white"],
            ["Open Orders", stats.open, "text-amber-400"],
            ["Awaiting Delivery", stats.awaiting, "text-indigo-300"],
            ["Received", stats.received, "text-emerald-400"],
          ].map(([k, v, c]) => (
            <div
              key={k}
              data-testid={`po-stat-${k.toLowerCase().replace(/ /g, "-")}`}
              className="rounded-lg border border-zinc-800 bg-zinc-900 p-4"
            >
              <div className="text-[10px] uppercase tracking-widest text-zinc-500 sm:text-xs">
                {k}
              </div>
              <div className={`font-mono-data mt-1 text-xl font-bold ${c}`}>
                {v}
              </div>
            </div>
          ))}
        </div>

        <div className="mt-6 flex flex-col gap-3 lg:flex-row lg:items-start">
          <FilterBar
            className="min-w-0 flex-1"
            testidPrefix="pos"
            search={{
              value: q,
              onChange: setQ,
              placeholder: "Search PO, supplier, color…",
              testid: "po-search-input",
            }}
            sections={[
              {
                key: "status",
                label: "Status",
                multi: true,
                values: filter,
                allValue: "All",
                onChange: setFilter,
                options: FILTERS.map((f) => [
                  f,
                  f,
                  `po-filter-${f.toLowerCase()}`,
                ]),
              },
            ]}
          />
          <div className="flex gap-2 overflow-x-auto pb-1">
            <div
              className="flex shrink-0 overflow-hidden rounded-md border border-zinc-800"
              data-testid="po-view-toggle"
            >
              {VIEWS.map((v) => (
                <button
                  key={v}
                  data-testid={`po-view-${v.toLowerCase()}`}
                  onClick={() => setView(v)}
                  className={`h-12 px-3 text-[11px] font-semibold uppercase tracking-widest transition-colors duration-150 ${
                    view === v
                      ? "bg-indigo-500 text-white"
                      : "bg-zinc-900 text-zinc-400 hover:bg-zinc-800"
                  }`}
                >
                  {v}
                </button>
              ))}
            </div>
          </div>
        </div>

        {shown.length === 0 && (
          <div
            data-testid="po-empty-state"
            className="mt-4 rounded-lg border border-dashed border-zinc-800 p-10 text-center"
          >
            <Package
              size={26}
              weight="bold"
              className="mx-auto text-zinc-700"
            />
            <p className="mt-3 text-sm text-zinc-400">
              No purchase orders match this view.
            </p>
          </div>
        )}

        {view === "Cards" && shown.length > 0 && (
          <div className="mt-4 space-y-3" data-testid="po-list">
            {paged.map((po) => {
              const { total, got, pct } = lineCounts(po);
              const sinkLines = po.lines.filter(
                (l) => l.kind === "sink",
              ).length;
              return (
                <Link
                  key={po.id}
                  to={`/inventory/purchase-orders/${po.id}`}
                  data-testid={`po-card-${po.id}`}
                  className="group relative flex flex-wrap items-center gap-4 overflow-hidden rounded-lg border border-zinc-800 bg-zinc-900 p-4 pl-5 transition-all duration-150 hover:-translate-y-0.5 hover:border-indigo-400/40"
                >
                  <span
                    className={`absolute left-0 top-0 h-full w-1.5 ${PO_ACCENT[po.status] || PO_ACCENT.Draft}`}
                  />
                  <div className="min-w-0 flex-1">
                    <div className="font-mono-data text-xs text-indigo-300">
                      {po.code}
                    </div>
                    <h3 className="truncate text-base font-bold">
                      {po.supplier_name || "No supplier"}
                    </h3>
                    {po.customer_name && (
                      <div
                        data-testid={`po-customer-${po.id}`}
                        className="truncate text-xs text-indigo-300"
                      >
                        For {po.customer_name}
                        {po.job_code ? ` · ${po.job_code}` : ""}
                      </div>
                    )}
                    <div className="mt-2 flex flex-wrap items-center gap-2">
                      {po.rush && (
                        <span
                          data-testid={`po-rush-badge-${po.id}`}
                          className="rounded-full border border-red-500/40 bg-red-500/15 px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-widest text-red-300"
                        >
                          🔥 Rush
                        </span>
                      )}
                      <span className="rounded-full border border-zinc-800 bg-zinc-950 px-2.5 py-0.5 text-[10px] uppercase tracking-widest text-zinc-400">
                        {po.lines.length} line{po.lines.length === 1 ? "" : "s"}
                      </span>
                      {sinkLines > 0 && (
                        <span className="rounded-full border border-indigo-400/25 bg-indigo-500/10 px-2.5 py-0.5 text-[10px] uppercase tracking-widest text-indigo-300">
                          {sinkLines} sink line{sinkLines === 1 ? "" : "s"}
                        </span>
                      )}
                      {po.expected_date && (
                        <span className="font-mono-data inline-flex items-center gap-1 rounded-full border border-zinc-800 bg-zinc-950 px-2.5 py-0.5 text-[10px] text-zinc-400">
                          <CalendarBlank size={11} weight="bold" />{" "}
                          {po.expected_date}
                        </span>
                      )}
                      {po.reference && (
                        <span className="font-mono-data rounded-full border border-zinc-800 bg-zinc-950 px-2.5 py-0.5 text-[10px] text-zinc-500">
                          ref {po.reference}
                        </span>
                      )}
                      {po.consignment && (
                        <span className="rounded-full border border-amber-500/25 bg-amber-500/10 px-2.5 py-0.5 text-[10px] uppercase tracking-widest text-amber-400">
                          Consignment
                        </span>
                      )}
                    </div>
                  </div>

                  <div className="w-full sm:w-44">
                    <div className="flex items-baseline justify-between">
                      <span className="font-mono-data text-sm">
                        <span className="text-indigo-300">{got}</span>
                        <span className="text-zinc-500"> / {total}</span>
                      </span>
                      <span className="text-[10px] uppercase tracking-widest text-zinc-600">
                        received
                      </span>
                    </div>
                    <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-zinc-800">
                      <div
                        className={`h-full rounded-full transition-all duration-300 ${
                          pct === 100 ? "bg-emerald-500" : "bg-indigo-500"
                        }`}
                        style={{ width: `${pct}%` }}
                      />
                    </div>
                  </div>

                  {po.supplier_notified_at && (
                    <span
                      data-testid={`po-emailed-${po.id}`}
                      title={`Emailed to ${po.supplier_notified_to} on ${po.supplier_notified_at.slice(0, 10)}`}
                      className="inline-flex shrink-0 items-center gap-1 rounded-full border border-emerald-500/25 bg-emerald-500/10 px-2 py-1 text-[10px] uppercase tracking-widest text-emerald-300"
                    >
                      <PaperPlaneTilt size={10} weight="bold" /> Sent
                    </span>
                  )}
                  <span
                    className={`shrink-0 rounded-full border px-3 py-1 text-[10px] font-semibold uppercase tracking-widest ${
                      PO_BADGE[po.status] || PO_BADGE.Draft
                    }`}
                  >
                    {po.status}
                  </span>
                </Link>
              );
            })}
          </div>
        )}

        {view === "Table" && shown.length > 0 && (
          <div
            className="mt-4 overflow-x-auto rounded-lg border border-zinc-800 bg-zinc-900"
            data-testid="po-table"
          >
            <table className="w-full text-left text-sm">
              <thead>
                <tr className="border-b border-zinc-800 text-[10px] uppercase tracking-widest text-zinc-500">
                  <th className="p-3">PO</th>
                  <th className="p-3">Supplier</th>
                  <th className="p-3 text-right">Lines</th>
                  <th className="p-3 text-right">Received</th>
                  <th className="p-3">Expected</th>
                  <th className="p-3">Status</th>
                </tr>
              </thead>
              <tbody>
                {paged.map((po) => {
                  const { total, got } = lineCounts(po);
                  return (
                    <tr
                      key={po.id}
                      data-testid={`po-row-${po.id}`}
                      className="border-b border-zinc-800 last:border-0 hover:bg-zinc-950/60"
                    >
                      <td className="font-mono-data p-3 text-indigo-300">
                        <Link
                          to={`/inventory/purchase-orders/${po.id}`}
                          data-testid={`po-open-${po.id}`}
                        >
                          {po.code}
                        </Link>
                      </td>
                      <td className="p-3 font-semibold">
                        {po.supplier_name || "—"}
                        {po.customer_name && (
                          <div className="text-xs font-normal text-indigo-300">
                            For {po.customer_name}
                            {po.job_code ? ` · ${po.job_code}` : ""}
                          </div>
                        )}
                      </td>
                      <td className="font-mono-data p-3 text-right text-zinc-400">
                        {po.lines.length}
                      </td>
                      <td className="font-mono-data p-3 text-right">
                        <span className="text-indigo-300">{got}</span>
                        <span className="text-zinc-500"> / {total}</span>
                      </td>
                      <td className="font-mono-data p-3 text-zinc-400">
                        {po.expected_date || "—"}
                      </td>
                      <td className="p-3">
                        <span
                          className={`rounded-full border px-2.5 py-0.5 text-[10px] font-semibold uppercase tracking-widest ${
                            PO_BADGE[po.status] || PO_BADGE.Draft
                          }`}
                        >
                          {po.status}
                        </span>
                        {po.supplier_notified_at && (
                          <span
                            data-testid={`po-emailed-${po.id}`}
                            title={`Emailed to ${po.supplier_notified_to} on ${po.supplier_notified_at.slice(0, 10)}`}
                            className="ml-2 inline-flex items-center gap-1 rounded-full border border-emerald-500/25 bg-emerald-500/10 px-2 py-0.5 text-[10px] uppercase tracking-widest text-emerald-300"
                          >
                            <PaperPlaneTilt size={10} weight="bold" /> Sent
                          </span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
        <Pagination page={curPage} total={shown.length} onPage={setPage} testId="purchase-orders-pagination" />
      </main>
    </div>
  );
}
